import { useMemo } from "react";
import { extractRecentTrades, extractPerpTrade, extractPredictionTradeItem, extractStandardizedInstrument, extractWalletProfile, } from "@orb-labs/orby-core";
import { notEmpty } from "@orb-labs/types";
import { ListSignalsQuery } from "../../graphql/queries.js";
import { useBullrunUseQuery } from "../../hooks/bullrunQueries/BullrunUseQueryContext.js";
/**
 * Convert a raw GraphQL `SignalsItem` to its SDK-shaped equivalent.
 * Branches `tradeItem` on `__typename` and routes through the same
 * per-type extractors `useGetActivityHistoryQuery` uses, so the row
 * renderers can share `isTokenTrade` / `isPerpTrade` /
 * `isPredictionTradeItem` guards across the two surfaces.
 *
 * `WalletProfile.accounts: [Account!]!` is a list on the wire to admit
 * cluster wallets that share a profile; the Signal feed surfaces one
 * row per trader, so we collapse to `accounts[0]` (the primary account)
 * for the SDK shape. Rows without a primary account are dropped — they
 * carry no clickable target.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractSignalEdge(raw) {
    if (!raw?.activityItem || !raw?.profile) {
        return undefined;
    }
    // The SDK extractors hard-invariant on required fields (e.g. Currency
    // throws "DECIMALS" when `decimals` is missing). One malformed edge must
    // degrade to a logged drop, not propagate out of the consumers' render
    // `.map` and error-boundary the entire For You feed — which is exactly
    // what happened when the slim signal-feed fragment omitted the perp
    // currency `decimals`.
    try {
        const typename = raw.activityItem.__typename;
        let tradeItem;
        if (typename === "Trade") {
            [tradeItem] = extractRecentTrades([raw.activityItem]);
        }
        else if (typename === "PerpTrade") {
            tradeItem = extractPerpTrade(raw.activityItem);
        }
        else if (typename === "PredictionTradeItem") {
            const item = raw.activityItem;
            tradeItem = extractPredictionTradeItem({
                ...item,
                quantity: item.predQuantity ?? item.quantity,
                fiatValue: item.predFiatValue ?? item.fiatValue,
                transactionHash: item.predTransactionHash ?? item.transactionHash,
            });
        }
        if (!notEmpty(tradeItem))
            return undefined;
        const primary = Array.isArray(raw.profile.accounts) ? raw.profile.accounts[0] : undefined;
        if (!notEmpty(primary))
            return undefined;
        const convictionMultiple = Number(raw.convictionMultiple);
        return {
            id: raw.id,
            instrumentType: String(raw.instrumentType),
            profile: extractWalletProfile(raw.profile),
            instrument: extractStandardizedInstrument(raw.instrument),
            tradeItem,
            convictionMultiple: Number.isFinite(convictionMultiple) && convictionMultiple > 0 ? convictionMultiple : undefined,
        };
    }
    catch (error) {
        console.error("[extractSignalEdge] dropping malformed edge", {
            id: raw?.id,
            instrumentType: raw?.instrumentType,
            activityTypename: raw?.activityItem?.__typename,
            error,
        });
        return undefined;
    }
}
const DEFAULT_PAGE_SIZE = 25;
/**
 * Build the wire `ListSignalsInput` from hook-level input. Exported so
 * `useSignalFeedItems`' `fetchMore` / `refetch` calls send EXACTLY the same
 * input shape (same omit-when-default treatment, same cache keyArgs subfields)
 * as the watched query — a fetchMore whose input drops e.g. `feedMode` or
 * `includeClusters` requests the wrong result set AND lands in a different
 * cache entry, so the watched list never updates and infinite scroll spins
 * forever.
 */
export function buildListSignalsInput(input = {}) {
    const { limit, offset, followingOnly, instrumentTypes, feedMode, includeClusters, minNotionalUsd } = input;
    return {
        limit: limit ?? DEFAULT_PAGE_SIZE,
        offset: offset ?? 0,
        followingOnly: followingOnly ?? false,
        // Empty/null means "no instrument filter" server-side; omit the field
        // entirely when empty so each tab's cache key (see `listSignals`
        // keyArgs) stays clean and we don't send a redundant variable.
        ...(notEmpty(instrumentTypes) && instrumentTypes.length > 0
            ? { instrumentTypes: instrumentTypes }
            : {}),
        // Same omit-when-default treatment for the noise controls so legacy
        // callers keep their existing cache keys.
        ...(notEmpty(feedMode) ? { feedMode } : {}),
        ...(includeClusters === true ? { includeClusters: true } : {}),
        ...(notEmpty(minNotionalUsd) && minNotionalUsd > 0 ? { minNotionalUsd } : {}),
    };
}
/**
 * Fetch policies for the Signal feed query, exported so the regression test
 * (`__tests__/graphql/listSignalsFetchPolicy.test.ts`) exercises the exact
 * combination the hook ships with. See the comment at the use site below for
 * why this pairing (and not plain cache-first) is load-bearing.
 */
export const LIST_SIGNALS_FETCH_POLICY = "cache-and-network";
export const LIST_SIGNALS_NEXT_FETCH_POLICY = "cache-first";
/**
 * Apollo-backed hook for the Signal feed. Returns SDK-shaped
 * `ExtractedSignalEdge[]` (rather than raw codegen types) so screens can
 * read trader / instrument / trade fields with proper SDK types and the
 * existing `isTokenTrade` / `isPerpTrade` / `isPredictionTradeItem` guards.
 */
export function useListSignalsQuery(input = {}) {
    const useQueryLike = useBullrunUseQuery();
    const { data, error, loading, refetch, fetchMore } = useQueryLike(ListSignalsQuery, {
        // cache-and-network on mount + variables change, cache-first between.
        // The `listSignals` merge policy stores only the LAST fetched page per tab
        // (replace, not accumulate — see `cache.ts`), so a plain cache-first query
        // revisiting a tab is served a stale mid-feed page and never refetches; if
        // that page carried `hasNextPage: false`, the consumers' fetchMore guard
        // froze infinite scroll for the tab until a full reload. cache-and-network
        // still paints the cached page instantly (consumers gate empty/initial
        // states on `edges.length`, so the background refresh doesn't blank the
        // feed) while resetting the slot to a fresh page 1. nextFetchPolicy keeps
        // steady-state reads (fetchMore cache writes, subscriptions) cache-first;
        // Apollo restores the initial policy whenever variables change (tab
        // switch / toggle), which is exactly the revisit-refresh we need.
        fetchPolicy: LIST_SIGNALS_FETCH_POLICY,
        nextFetchPolicy: LIST_SIGNALS_NEXT_FETCH_POLICY,
        notifyOnNetworkStatusChange: true,
        variables: {
            // Built by the shared builder so paginating callers (`fetchMore` /
            // `refetch` in `useSignalFeedItems`) can construct byte-identical
            // inputs — see `buildListSignalsInput`.
            input: buildListSignalsInput(input),
        },
    });
    const hasNextPage = data?.listSignals?.pageInfo?.hasNextPage ?? false;
    const totalCount = data?.listSignals?.pageInfo?.totalCount ?? 0;
    const rawEdges = data?.listSignals?.edges;
    const edges = useMemo(() => {
        if (!rawEdges)
            return [];
        return rawEdges.map(extractSignalEdge).filter(notEmpty);
    }, [rawEdges]);
    return {
        loading,
        error,
        refetch,
        fetchMore,
        hasNextPage,
        totalCount,
        edges,
        /**
         * Unmapped wire rows for consumers that render the trade + cluster
         * union via `extractSignalFeedItem` (`signalFeedItems.ts`). `edges`
         * above stays trades-only for legacy callers; mapping the union here
         * would create an import cycle with the extractor module.
         */
        rawEdges: rawEdges ?? [],
    };
}
